/**
 * RoutineHeader — the single page-level header for the routine split view (P12 §6.1).
 *
 * Consolidates the two former section headers — the chat pane's "RoutineBuilder" strip and the editor
 * pane's title/description/action bar — into ONE bar that spans the whole page. The name + description
 * render as plain text; editing them happens in the Rename modal reached from the header's ⋯ context
 * menu (which also holds Reset conversation). Action order, left → right: [ Discard · Save ] · Run · ⋯.
 * The draft + actions come from {@link useRoutineDraftContext} (the store the chat and editor share);
 * the chat-only Reset control comes in as props.
 */
import { Check, Eye, MoreVertical, Pencil, Play, RotateCcw, Save, Send, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { NeedsRepairBadge } from '@/_shared/components/AccountRepair/NeedsRepairBadge';
import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { ConfirmDialog } from '@/_shared/components/ConfirmDialog';
import { Badge } from '@/_shared/components/ui/badge';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList } from '@/_shared/components/ui/breadcrumb';
import { Button, ButtonGroup } from '@/_shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';
import { Input } from '@/_shared/components/ui/input';
import { Label } from '@/_shared/components/ui/label';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { Textarea } from '@/_shared/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/_shared/components/ui/tooltip';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';

import { ROUTINE_DESCRIPTION_MAX, ROUTINE_NAME_MAX } from '../constants';
import { useRoutineDraftContext } from '../contexts/RoutineDraftContext';
import { REPAIR_AND_RESUME_FIRST, workflowRepairState } from '../lib/repair';

interface RoutineHeaderProps {
  /** Agent mid-turn → the routine can't be renamed (the agent owns the draft). */
  agentActive: boolean;
  /** Clear the RoutineBuilder conversation. */
  onResetChat: () => void;
  /** Enable the Reset-conversation menu item (only once the chat has messages). */
  canResetChat: boolean;
}

export function RoutineHeader({ agentActive, onResetChat, canResetChat }: RoutineHeaderProps): React.ReactElement {
  const { store, actions } = useRoutineDraftContext();
  const { wp, isAdmin } = useWorkspace();
  const navigate = useNavigate();
  const { draft, saveState, isLoading, workflowStatus, hasWorkingCopy } = store;

  const [renameOpen, setRenameOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);

  // Keep the Discard confirm open+locked while the delete is in flight, then close it once the mutation
  // settles (success or error) — so its confirm-button spinner isn't dead and the user can't double-fire.
  const wasDiscarding = useRef(false);
  useEffect(() => {
    if (wasDiscarding.current && !actions.isDiscarding) setDiscardOpen(false);
    wasDiscarding.current = actions.isDiscarding;
  }, [actions.isDiscarding]);

  const isReadOnly = agentActive;
  const isSaved = !!store.workflowId;
  const isRunning = actions.dryRun.isRunning;

  // A brand-new routine lives only in memory until the first Save. The breadcrumb below navigates
  // in-SPA, which the `beforeunload` guard can't intercept, so leaving that way would silently drop
  // the draft + conversation — confirm first. (This guards the breadcrumb specifically; the app uses
  // a component <BrowserRouter>, so a global useBlocker isn't available without a data-router migration.)
  const hasUnsavedNewWork = !isSaved && (saveState === 'unsaved' || canResetChat);

  // Breadcrumb back to the list — matches the dashboard detail page.
  const breadcrumb = (
    <Breadcrumb className="mb-4">
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link
              to={wp('/workflows')}
              onClick={(e) => {
                if (hasUnsavedNewWork) {
                  e.preventDefault();
                  setLeaveOpen(true);
                }
              }}
            >
              Workflows
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );

  if (isLoading) {
    return (
      <div className="border-b border-border px-6 py-4">
        {breadcrumb}
        <Skeleton className="h-7 w-64" />
        <Skeleton className="mt-2 h-4 w-80" />
      </div>
    );
  }

  // "Send to Slack" (real run) is meaningless for an unsaved routine and rejected for a draft — branch the
  // reason so a never-saved routine is told to Save (not "Activate", which it can't do yet).
  // ACT2-1286: no Run now while the repair pause is set — not even the admin override for a paused routine.
  const repair = workflowRepairState(store.accountRepair);
  const canSendToSlack = isSaved && workflowStatus !== 'draft' && !repair.repairPaused;
  const sendReason = !isSaved
    ? 'Save the routine first to send it.'
    : repair.repairPaused
      ? `${REPAIR_AND_RESUME_FIRST}.`
      : 'Activate the routine to send.';

  // Same "has pending changes" signal the "Unsaved changes" badge uses. Field edits flip saveState, so
  // Save re-enables after any edit. A modal rename (saved routines only) persists immediately via
  // actions.rename and deliberately does NOT dirty this.
  const hasUnsavedChanges = saveState === 'unsaved' || hasWorkingCopy;
  // "Saved" (disabled) when a persisted routine has nothing pending. The `saveState === 'saved'` term
  // keeps Save disabled in the window right after a NEW routine's first save, before react-router
  // updates the URL param (so `isSaved` is still false) — otherwise Save re-enables and a double-click
  // fires a second metered create. It can't disagree with the "Unsaved changes" badge: the autosave
  // supersession guard (useRoutineActions) prevents `hasWorkingCopy` from turning back on after a save.
  const isClean = saveState === 'saved' || (isSaved && !hasUnsavedChanges);
  const canSave = !actions.isSaving && !isClean;
  // A live routine keeps running the saved version until Save — surfaced on any pending change
  // (incl. a working-copy-only dirty state), not just `saveState === 'unsaved'`.
  const isLiveWithUnsavedEdits = isSaved && workflowStatus === 'active' && hasUnsavedChanges;
  const saveReason = isLiveWithUnsavedEdits
    ? 'The live routine is still running the saved version. Save to apply your edits.'
    : null;

  const showDiscard = isSaved && hasUnsavedChanges;

  // Renaming a non-draft (active/paused) routine is admin-only server-side (workflows.ts) — members can
  // only rename drafts. Mirror Delete: show it disabled with the reason rather than let it 403 on Save.
  const renameNeedsAdmin = (workflowStatus === 'active' || workflowStatus === 'paused') && !isAdmin;

  const saveButton = (
    <Button
      variant="primary"
      size="md"
      onClick={actions.save}
      disabled={!canSave}
      loading={actions.isSaving}
      Icon={isClean ? <Check /> : <Save />}
    >
      {isClean ? 'Saved' : 'Save'}
    </Button>
  );

  return (
    <>
      <div className="border-b border-border px-6 py-4">
        {breadcrumb}
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-0">
          {/* Name + description as plain text — editing lives in the Rename modal (⋯ menu). */}
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <h1 className="min-w-0 truncate text-lg font-semibold text-foreground">
                {/* Fallback matches the save path (useRoutineActions) so the title shown == the name persisted. */}
                {draft.name || 'New routine'}
              </h1>
              {repair.repairPaused && (
                <Badge variant={repair.broken ? 'warning' : 'info'} bordered className="shrink-0">
                  {repair.broken ? 'Paused: warehouse account removed' : 'Repaired. Resume to run again.'}
                </Badge>
              )}
              {!repair.repairPaused && repair.broken && <NeedsRepairBadge state="needs_repair" className="shrink-0" />}
              {isSaved ? (
                showDiscard && (
                  <Badge variant="warning" bordered className="shrink-0">
                    Unsaved changes
                  </Badge>
                )
              ) : (
                <Badge variant="info" className="shrink-0">
                  Draft
                </Badge>
              )}
            </div>
            {draft.description && (
              <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{draft.description}</p>
            )}
          </div>

          {/* Actions, left → right: [ Discard · Save ] · Run · ⋯ — wrap below the title on small screens. */}
          <div className="flex shrink-0 flex-wrap items-center gap-2 self-end md:ml-4 md:self-auto">
            <ButtonGroup>
              {showDiscard && (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => setDiscardOpen(true)}
                  loading={actions.isDiscarding}
                >
                  Discard
                </Button>
              )}
              {saveReason ? (
                <Tooltip>
                  <TooltipTrigger asChild>{saveButton}</TooltipTrigger>
                  <TooltipContent>{saveReason}</TooltipContent>
                </Tooltip>
              ) : (
                saveButton
              )}
            </ButtonGroup>

            {/* Run — menu to choose Send-to-Slack (real run) vs Preview-only (dry run). Skips the cron. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="md" loading={isRunning || actions.isRunningNow} Icon={<Play />}>
                  Run
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel className="text-[0.6875rem] font-semibold tracking-wide text-muted-foreground uppercase">
                  Run once now — skips the schedule
                </DropdownMenuLabel>
                {/* Two-line items (title + description), so they opt out of the primitive's single-line h-8. */}
                <DropdownMenuItem
                  disabled={!canSendToSlack}
                  onSelect={() => {
                    actions.runDryRun(); // render the preview inline, so you see what was sent
                    actions.runNow(); // and actually post to Slack
                  }}
                  className="h-auto items-start gap-3 py-2"
                >
                  <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-grey-100 text-muted-foreground">
                    <Send className="size-3.5" />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">Send to Slack</span>
                    <span className="text-xs text-muted-foreground">
                      {canSendToSlack ? 'Run for real and post the message now.' : sendReason}
                    </span>
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => actions.runDryRun()} className="h-auto items-start gap-3 py-2">
                  <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-grey-100 text-muted-foreground">
                    <Eye className="size-3.5" />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">Preview only</span>
                    <span className="text-xs text-muted-foreground">Render the message without sending.</span>
                  </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Context menu — Rename (opens the modal) + Reset conversation. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="md" iconOnly aria-label="More actions" Icon={<MoreVertical />} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {/* Rename only exists once the routine is saved — a never-saved routine has no persisted
                    version to rename (it's named by the agent while authoring / on first Save). */}
                {isSaved && (
                  <div className={renameNeedsAdmin ? '[&>span]:block [&>span]:w-full' : undefined}>
                    <ActionTooltip show={renameNeedsAdmin} message="Only an admin can rename a routine.">
                      <DropdownMenuItem
                        disabled={renameNeedsAdmin || isReadOnly}
                        onSelect={() => setRenameOpen(true)}
                        className={renameNeedsAdmin ? 'w-full' : undefined}
                      >
                        <Pencil className="size-4" />
                        Rename
                      </DropdownMenuItem>
                    </ActionTooltip>
                  </div>
                )}
                <DropdownMenuItem disabled={!canResetChat} onSelect={onResetChat}>
                  <RotateCcw className="size-4" />
                  Reset conversation
                </DropdownMenuItem>
                {/* Delete a SAVED routine — nothing to delete for a never-saved draft (the "Leave without
                    saving" flow covers that). Delete is admin-only server-side; non-admins see it disabled
                    with the reason, rather than a missing affordance. */}
                {isSaved && (
                  <>
                    <DropdownMenuSeparator />
                    <div className={!isAdmin ? '[&>span]:block [&>span]:w-full' : undefined}>
                      <ActionTooltip show={!isAdmin} message="Only an admin can delete a routine.">
                        <DropdownMenuItem
                          variant="destructive"
                          disabled={!isAdmin}
                          onSelect={() => setDeleteOpen(true)}
                          className={!isAdmin ? 'w-full' : undefined}
                        >
                          <Trash2 className="size-4" />
                          Delete
                        </DropdownMenuItem>
                      </ActionTooltip>
                    </div>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <RenameRoutineDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        name={draft.name}
        description={draft.description}
        saving={actions.isRenaming}
        onSave={(name, description) => actions.rename(name, description)}
      />

      {/* Discard is a hard, unrecoverable delete of the working copy — confirm before running it. */}
      <ConfirmDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title="Discard unsaved changes?"
        description="This reverts to the last saved version of the routine and permanently drops your unsaved edits. This can't be undone."
        cancelLabel="Keep editing"
        confirmLabel="Discard changes"
        loading={actions.isDiscarding}
        onConfirm={() => actions.discardDraft()}
      />

      {/* Permanently delete a saved routine (admin-only) — its runs + authoring conversation cascade. */}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this routine?"
        description="This permanently deletes the routine, its run history, and its RoutineBuilder conversation. This can't be undone."
        cancelLabel="Keep editing"
        confirmLabel="Delete"
        loading={actions.isRemoving}
        onConfirm={() => actions.remove()}
      />

      {/* Leaving a never-saved routine via the breadcrumb drops the in-memory draft + conversation. */}
      <ConfirmDialog
        open={leaveOpen}
        onOpenChange={setLeaveOpen}
        title="Leave without saving?"
        description="This routine hasn't been saved yet. Leaving now discards the draft and the conversation. This can't be undone."
        cancelLabel="Keep editing"
        confirmLabel="Leave"
        onConfirm={() => {
          setLeaveOpen(false);
          void navigate(wp('/workflows'));
        }}
      />
    </>
  );
}

interface RenameRoutineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  description: string;
  /** True while the rename API call is in flight — locks the dialog and drives the Save button spinner. */
  saving: boolean;
  /** Persists the rename; resolves on success, rejects on failure. */
  onSave: (name: string, description: string) => Promise<void>;
}

/** Rename modal — edits the routine name + description; persists on Save and closes only once it succeeds. */
function RenameRoutineDialog({
  open,
  onOpenChange,
  name,
  description,
  saving,
  onSave,
}: RenameRoutineDialogProps): React.ReactElement {
  return (
    // Locked while the rename is in flight — ignore outside-click / Escape so it can't be dismissed mid-save.
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename routine</DialogTitle>
          <DialogDescription>Update the routine&apos;s name and description.</DialogDescription>
        </DialogHeader>
        {/* The form mounts fresh on each open (Radix unmounts closed content), so its fields seed from
            the current draft without a set-state-in-effect. */}
        <RenameForm
          name={name}
          description={description}
          saving={saving}
          onCancel={() => onOpenChange(false)}
          onSave={async (nextName, nextDescription) => {
            // Wait for the API call to succeed before closing; on failure keep the dialog open (the
            // mutation's onError already surfaced a toast).
            try {
              await onSave(nextName, nextDescription);
              onOpenChange(false);
            } catch {
              /* stay open for a retry */
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

interface RenameFormProps {
  name: string;
  description: string;
  saving: boolean;
  onCancel: () => void;
  onSave: (name: string, description: string) => Promise<void>;
}

function RenameForm({ name, description, saving, onCancel, onSave }: RenameFormProps): React.ReactElement {
  const [nameValue, setNameValue] = useState(name);
  const [descriptionValue, setDescriptionValue] = useState(description);

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="rename-name">Name</Label>
          <Input
            id="rename-name"
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value.slice(0, ROUTINE_NAME_MAX))}
            maxLength={ROUTINE_NAME_MAX}
            placeholder="Untitled routine"
            disabled={saving}
            autoFocus
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="rename-description">Description</Label>
          <Textarea
            id="rename-description"
            value={descriptionValue}
            onChange={(e) => setDescriptionValue(e.target.value.slice(0, ROUTINE_DESCRIPTION_MAX))}
            maxLength={ROUTINE_DESCRIPTION_MAX}
            placeholder="Describe what this routine does (optional)…"
            rows={3}
            className="resize-none"
            disabled={saving}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        {/* Save persists the rename via the API; the dialog stays open (spinner) until it succeeds.
            Disabled on an empty name (the server requires min 1). Caps match the server Zod limits —
            never silently truncate an existing name that was within bounds. */}
        <Button
          variant="primary"
          loading={saving}
          disabled={nameValue.trim().length === 0}
          onClick={() =>
            void onSave(
              nameValue.trim().slice(0, ROUTINE_NAME_MAX),
              descriptionValue.trim().slice(0, ROUTINE_DESCRIPTION_MAX),
            )
          }
        >
          Save
        </Button>
      </DialogFooter>
    </>
  );
}
